import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { useCallback, useState } from 'react';
import ConsoleCard from './ConsoleCard';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { selectCurrentTaskAIStatus } from '../../redux/features/taskRouter/taskRouter.selector';
import {
  selectEventAPI,
  selectEventAPIAuth,
  selectIsHitl7Enabled,
} from '../../redux/features/config/config.selector';
import { selectActiveRestaurantCode } from '../../selectors/restaurant';
import { selectUserProfile } from '../../selectors/user';
import { UserDetails } from '../../types';
import { TextFrameTransmissionMessage } from '../../reducers/messagingSlice';
import Colors from '../../utils/color';
import { HITL3_PADDING } from '../../utils/hitl3Spacing';
import { HITL3_FONT_SIZE } from '../../utils/hitl3Typography';
import ConfirmDialog from '../common/ConfirmDialog';
import { MessageContainer } from '../Chat/MessageContainer';
import useTranscriptMessages from '../TranscriptSliderArea/useTranscriptMessages.hooks';
import PulseAdvise from '../CustomerPulse/PulseAdvise';
import { useDisplayPreference } from '../Layout/DisplayPreferenceContext';
import { sendAsrIncorrectEvent } from '../../utils/asrEvents';
import useIssueReport from '../../hooks/issueReport/useIssueReport.hooks';
import { ASR_MISSING_ISSUE_TYPE } from '../../constants/issueTypes';
import useIntervention from '../../hooks/intervention/useIntervention.hooks';
import useStickyScroll from '../../hooks/useStickyScroll.hooks';
import { selectActiveSuggestions } from '../../redux/features/aiSuggestions/aiSuggestions.selector';

// Stable empty list so blanking the transcript during staff intervention doesn't
// hand useStickyScroll a fresh array identity on every render.
const NO_MESSAGES: TextFrameTransmissionMessage[] = [];

const useStyles = makeStyles<Theme>(({ spacing }) => ({
  // Transcript section: heading + inner white card. The grey card backdrop is
  // the shared left-pane card (MainPageLayout), so this stays transparent. The
  // takeover expansion is applied to that card (not this section), so the
  // reminder + AI status sections stay visible alongside the transcript.
  section: {
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    // Anchor for the "New messages" pill, which floats over the message stream.
    position: 'relative',
  },
  // The transcript message stream, below the header divider (supplied by
  // ConsoleCard). The body just adds the matching top breathing room below that
  // line, and horizontal room for the bubbles' tails.
  messages: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    // Horizontal room for the bubbles' protruding WhatsApp tails (they extend
    // ~6px past the bubble edge); overflowX hidden so that never shows an
    // x-scrollbar. Clipping is at the padding box, so the tails stay visible.
    overflowX: 'hidden',
    padding: HITL3_PADDING,
  },
  dividerContainer: {
    display: 'flex',
    alignItems: 'center',
    paddingBottom: spacing(1),
  },
  divider: {
    fontSize: '14px',
    lineHeight: '20px',
    color: Colors.guardsmanRed,
  },
  underline: {
    flex: 1,
    padding: '0.75px',
    backgroundColor: Colors.guardsmanRed,
    margin: '5px',
    display: 'inline-block',
  },
  // Shown only while the agent has scrolled away from the live edge, so they
  // know updates are still arriving; clicking it returns them to following.
  newMessagesPill: {
    position: 'absolute',
    bottom: HITL3_PADDING,
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 3,
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    border: 'none',
    borderRadius: '999px',
    padding: `4px ${HITL3_PADDING}`,
    cursor: 'pointer',
    backgroundColor: Colors.readBack,
    color: Colors.white,
    fontSize: HITL3_FONT_SIZE,
    fontWeight: 600,
    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.25)',
  },
  newMessagesIcon: {
    fontSize: '1rem',
  },
}));

/**
 * HITL-1667 (hitl_7_0): the transcript pane WITHOUT the ConsoleCard header.
 *
 * Mirrors ConsoleCard's own `card` rule exactly, minus the header row: the same
 * flush white column that fills its panel and scrolls internally. Written out
 * here rather than added as a `hideHeader` prop on ConsoleCard because that
 * component is the shell for six other cards, and a rule added to its styles
 * would renumber JSS class names across unrelated snapshot suites.
 *
 * The heading goes because the pane is unmistakable without it — it is the only
 * thing in the console that streams speech — and the row it occupied is worth
 * two more lines of conversation, which is the same trade the rest of this
 * ticket makes. The chevron goes with it; the panel is still drag-resizable, so
 * the agent can still give the space to the Pulse strip below.
 */
/**
 * HITL-1669: the "New messages" pill takes the header's own colour under 7.0.
 *
 * Inline rather than a change to the `newMessagesPill` rule below, because that
 * rule is shared with the 3.0 pane and must keep its `Colors.readBack` there —
 * only the 7.0 console is being retuned to the header colour. Inline also beats
 * the class, which sets the same property.
 *
 * Same move as the Order Flow pills (HITL-1665) and the suggestion box: the
 * header and the full-width status band are permanent horizontal bands, so a
 * second blue floating over the transcript read as a separate design language.
 */
const HITL7_NEW_MESSAGES_STYLE: React.CSSProperties = {
  backgroundColor: Colors.daintree,
};

// HITL-1673 (hitl_7_0): 4px of horizontal inset instead of the shared 8px. The
// transcript is the narrowest of the three panes and its lines are left-aligned
// text with nothing at their edges, so the outer 4px a side bought nothing and
// cost every line its width. Vertical stays at 8px: that IS the breathing room
// between the stream and the pane's edges.
//
// An inline style rather than a second `makeStyles` rule, following
// HITL7_PANE_STYLE below: this file uses `@mui/styles`, whose class names are
// counter-numbered, so adding a rule here renumbers them in unrelated snapshot
// suites.
const HITL7_MESSAGES_STYLE: React.CSSProperties = {
  paddingLeft: 4,
  paddingRight: 4,
};

const HITL7_PANE_STYLE: React.CSSProperties = {
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: Colors.white,
  overflow: 'hidden',
};

/**
 * LIVE TRANSCRIPT (left pane). A single card: header (chat icon + label +
 * accordion chevron) above an inset divider, then the AI/customer message
 * stream (supervised-agent divider + click-to-report ASR-missing flow).
 * Collapsing via the chevron hides the stream, leaving just the header.
 *
 * NOTE: the volume control has been moved out of here for now.
 */
interface LiveTranscriptCardProps {
  // Accordion collapse, mirroring Customer Pulse / Action Center. Controlled
  // (open + onOpenChange) so the parent can sync the resizable panel; falls back
  // to internal state (default open) when used standalone.
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const LiveTranscriptCard = ({
  open: openProp,
  onOpenChange,
}: LiveTranscriptCardProps) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const currentTaskAIStatus = useAppSelector(selectCurrentTaskAIStatus);
  const isHitl7 = useAppSelector(selectIsHitl7Enabled);
  // HITL-1695: the 4px side padding below was cut for the plain line. The
  // bubble's WhatsApp tail protrudes ~6px past it and would clip, so a 7.0
  // agent who chose bubbles keeps the 3.0 padding the tail was built against.
  const { pref: displayPref } = useDisplayPreference();
  const isHitl7Line = isHitl7 && displayPref.transcriptStyle === 'plain';

  const [openState, setOpenState] = useState(true);
  const open = openProp ?? openState;
  const toggleOpen = () => {
    const next = !open;
    if (openProp === undefined) setOpenState(next);
    onOpenChange?.(next);
  };

  const eventAPI = useAppSelector(selectEventAPI);
  const eventAPIAuth = useAppSelector(selectEventAPIAuth);
  const restaurantCode = useAppSelector(selectActiveRestaurantCode);
  const userDetails = useAppSelector(selectUserProfile) as UserDetails | null;
  const effectiveSessionId = useAppSelector(
    (state) =>
      state.taskRouter.taskAssignedSessionId ||
      state.sessionBoundary.sessionId ||
      state.sessionBoundary.endedSessionId ||
      state.messages.currentSessionId
  );

  const { messagesList, sessionBufferMessagesList } = useTranscriptMessages();
  const { isStaffInterventionHappening } = useIntervention();
  // Staff intervention hands the conversation to a human, so the AI transcript is
  // no longer the live record — blank it out. This is a view-layer clear only:
  // the underlying frames stay in Redux, so if the intervention resolves the
  // stream reappears intact (no data destroyed).
  const displayedMessages = isStaffInterventionHappening
    ? NO_MESSAGES
    : messagesList;
  const displayedSessionBuffer = isStaffInterventionHappening
    ? NO_MESSAGES
    : sessionBufferMessagesList;
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  /**
   * HITL-1501: with issue reporting on, a blank-area click files a real issue report
   * instead of the legacy `asr_incorrect` event, and the confirm dialog goes with
   * it — the flag panel IS the confirmation, and it carries Undo.
   *
   * The legacy path is GATED, not deleted. The flag is per-store, so deleting it
   * would strip ASR reporting from every store that does not have the new feature
   * on. With the flag off everything below behaves exactly as it did.
   */
  const { isEnabled: isIssueReportingEnabled, flagIssue } = useIssueReport();

  // HITL-1481: the scroll container is this card's message stream, so follow-mode
  // is owned here rather than inside MessageContainer. `open` joins the session id
  // in the reset key because collapsing hides the stream with `display: none`,
  // which drops its scroll offset — there is nothing left to preserve, so a
  // re-expand lands back at the live edge.
  // HITL-1673: the inline AI suggestion (HITL-1668) renders INSIDE this stream,
  // but it was not among the hook's deps — so a suggestion arriving on its own,
  // with no message beside it, grew the content without anything telling the
  // hook to follow it. It appeared below the fold and stayed there.
  //
  // Keyed on `suggestion_id` rather than the array: `selectActiveSuggestions`
  // maps over the slice, so a primitive is what guarantees the scroll effect
  // cannot fire on an unrelated render.
  //
  // Empty with the flag off, where `PulseAdvise` is NOT in this stream (see the
  // render below) — there, a suggestion must not move the transcript at all.
  const activeSuggestions = useAppSelector(selectActiveSuggestions);
  const suggestionKey = isHitl7
    ? activeSuggestions.map((suggestion) => suggestion.suggestion_id).join('|')
    : '';

  // Following is conditional on the agent already being at the live edge — the
  // hook only jumps when `pinnedRef` is set, and otherwise raises the "new
  // messages" pill. A suggestion therefore behaves like every other thing that
  // lands here and never yanks the view away from someone reading back.
  const { scrollRef, handleScroll, isPinned, hasNewMessages, scrollToBottom } =
    useStickyScroll(
      [displayedMessages, displayedSessionBuffer, suggestionKey],
      `${effectiveSessionId}:${open}`
    );

  const handleTranscriptAreaClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      // During staff intervention the transcript is blanked, so there is no word
      // to report as ASR-incorrect — don't surface the "ASR MISSING" confirm.
      if (isStaffInterventionHappening) {
        return;
      }
      const target = event.target as HTMLElement | null;
      if (
        typeof document !== 'undefined' &&
        document.querySelector('[role="dialog"]')
      ) {
        return;
      }
      if (
        target &&
        (target.closest('[data-word]') ||
          target.closest('[role="dialog"]') ||
          // HITL-1668: the suggestion box lives inside this click target now.
          // Without this, accepting or dismissing a suggestion would ALSO file
          // a Missing Transcription issue report — the click bubbles out of the
          // buttons to here. closest() so the buttons and the badge are covered
          // by the one marker on the box.
          target.closest('[data-suggestion]'))
      ) {
        return;
      }
      if (isIssueReportingEnabled) {
        // PILOT DUAL-EMIT — same reasoning as the word-click path in
        // TranscriptMessage: the legacy `asr_incorrect` series stays continuous for
        // pilot stores instead of going silent under a different event type.
        sendAsrIncorrectEvent({
          eventAPI,
          eventAPIAuth,
          restaurantCode,
          effectiveSessionId,
          userDetails,
          wordClicked: 'ASR MISSING',
          dispatch,
        });
        // The gesture IS the classification — a click on the empty transcript means
        // "nothing was transcribed here" and nothing else — so the type rides on
        // revision 0 rather than waiting for the agent to pick it in the panel.
        //
        // TRADE-OFF, accepted deliberately: this used to be a two-step action behind
        // a dialog and is now one click on a large target, so a stray click files a
        // report. Undo covers it for the length of the flag window, and the one-open
        // -flag guard stops a run of them. Worth watching the pilot for a
        // Missing Transcription spike specifically.
        flagIssue({
          source: 'transcript_turn',
          issue_type: ASR_MISSING_ISSUE_TYPE,
        });
        return;
      }
      setConfirmDialogOpen(true);
    },
    [
      isStaffInterventionHappening,
      isIssueReportingEnabled,
      flagIssue,
      eventAPI,
      eventAPIAuth,
      restaurantCode,
      effectiveSessionId,
      userDetails,
      dispatch,
    ]
  );

  const handleConfirmBlankClick = useCallback(() => {
    sendAsrIncorrectEvent({
      eventAPI,
      eventAPIAuth,
      restaurantCode,
      effectiveSessionId,
      userDetails,
      wordClicked: 'ASR MISSING',
      dispatch,
    });
    setConfirmDialogOpen(false);
  }, [
    eventAPI,
    eventAPIAuth,
    restaurantCode,
    effectiveSessionId,
    userDetails,
    dispatch,
  ]);

  const stream = (
    <div
      className={classes.messages}
      style={isHitl7Line ? HITL7_MESSAGES_STYLE : undefined}
      data-transcript="true"
      ref={scrollRef}
      onScroll={handleScroll}
      onClick={handleTranscriptAreaClick}
    >
      {displayedSessionBuffer.length > 0 && (
        <MessageContainer messageList={displayedSessionBuffer} compact />
      )}
      {currentTaskAIStatus && displayedSessionBuffer.length > 0 && (
        <div className={classes.dividerContainer}>
          <div className={classes.underline} />
          <div className={classes.divider}>With Supervised Agent</div>
          <div className={classes.underline} />
        </div>
      )}
      <MessageContainer messageList={displayedMessages} compact />
      {/* HITL-1668: the AI's suggested reply, as the LAST entry in the
          conversation — the next thing that could be said, in the place the
          agent is already reading. It moved here out of the Copilot strip,
          which is why it is imported across from CustomerPulse; the component
          still owns its own display gates and the Alt+A / Alt+D shortcuts, and
          exactly one instance is ever mounted, so neither behaviour changes.

          Blanked during staff intervention like the rest of the stream: a
          suggestion describes an AI-run order that stopped applying the moment
          a human took it over. This mirrors the gate it had in the strip. */}
      {isHitl7 && !isStaffInterventionHappening && <PulseAdvise />}
    </div>
  );

  return (
    <div className={classes.section}>
      {isHitl7 ? (
        /* No header, so nothing is left to express "collapsed" except the panel's
           own size — which the agent sets by dragging. The stream therefore
           renders unconditionally rather than following `open`: a panel dragged
           short now shows the last couple of lines of the conversation, where
           before it showed a heading, and where gating on `open` would leave it
           blank. */
        <div style={HITL7_PANE_STYLE}>{stream}</div>
      ) : (
        <ConsoleCard
          icon={<ChatBubbleOutlineOutlinedIcon />}
          title="Transcript"
          open={open}
          onToggle={toggleOpen}
          // Keep the message stream mounted (hidden) so collapse/expand is instant
          // and messages aren't re-rendered from scratch on every expand.
          keepBodyMounted
        >
          {stream}
        </ConsoleCard>
      )}
      {/*
        Deliberately rendered outside the scrollable div above: that div's onClick
        opens the "ASR MISSING" confirm, and the pill must never trigger it.

        data-transcript marks this as a read-back control rather than an order
        interaction, so the agent-interception guards skip it. They listen on
        document in the capture phase, so nothing on this element could stop them.
      */}
      {(isHitl7 || open) && hasNewMessages && !isPinned && (
        <button
          type="button"
          data-testid="transcript-new-messages"
          data-transcript="true"
          className={classes.newMessagesPill}
          style={isHitl7 ? HITL7_NEW_MESSAGES_STYLE : undefined}
          onClick={scrollToBottom}
        >
          <KeyboardArrowDownIcon className={classes.newMessagesIcon} />
          New messages
        </button>
      )}
      <ConfirmDialog
        // Portal-rendered, so it needs the transcript marker itself — confirming
        // an ASR report is still not taking over the order.
        data-transcript="true"
        open={
          confirmDialogOpen &&
          !isStaffInterventionHappening &&
          !isIssueReportingEnabled
        }
        content={'Send ASR incorrect event for word: "ASR MISSING"?'}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={handleConfirmBlankClick}
        closeBtnText="Cancel"
        confirmBtnText="OK"
      />
    </div>
  );
};

export default LiveTranscriptCard;
